// The Social tab's creator section. Being a creator is the only way to have a public, searchable profile,
// and it is approval only: you apply with the @handle you want, Chefi decides. You can go private any time.
import React, { useCallback, useState } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import * as social from '../platform/social';
import { ApiError } from '../platform/auth';
import { usePalette } from '../theme';
import { useNav } from '../navigation';
import { confirm } from './Dialogs';
import { Body, Button, Caption, Field, Section } from './ui';
import type { MyCreator } from '../types';

export default function CreatorSection() {
  const { t } = useTranslation();
  const p = usePalette();
  const nav = useNav();
  const [mine, setMine] = useState<MyCreator | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ handle: '', displayName: '', bio: '', link: '' });
  const [bio, setBio] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useFocusEffect(useCallback(() => { social.myCreator().then(setMine, () => {}); }, []));
  const run = async (work: () => Promise<MyCreator>) => {
    setBusy(true); setError(null);
    try { setMine(await work()); return true; } catch (e) { setError(e instanceof ApiError ? e.code : 'generic'); return false; } finally { setBusy(false); }
  };
  const errorText = error ? t(`creators.errors.${error}` as 'creators.errors.generic', { defaultValue: t('creators.errors.generic') }) : null;
  const profile = mine?.profile, application = mine?.application;
  const applyForm = (
    <>
      <Field label={t('creators.handle')} value={form.handle} onChangeText={handle => setForm(f => ({ ...f, handle }))} placeholder="@nonna_rosa" autoCapitalize="none" autoCorrect={false} maxLength={31} />
      <Field label={t('creators.displayName')} value={form.displayName} onChangeText={displayName => setForm(f => ({ ...f, displayName }))} maxLength={60} />
      <Field label={t('creators.bio')} value={form.bio} onChangeText={b => setForm(f => ({ ...f, bio: b }))} multiline maxLength={300} />
      <Field label={t('creators.link')} value={form.link} onChangeText={link => setForm(f => ({ ...f, link }))} placeholder="https://www.instagram.com/…" autoCapitalize="none" autoCorrect={false} />
      <Button label={t('creators.apply')} icon="send" disabled={busy || !form.handle || !form.displayName.trim() || !form.bio.trim()} onPress={() =>
        run(() => social.applyCreator({ handle: form.handle, displayName: form.displayName, bio: form.bio, links: form.link.trim() ? [form.link.trim()] : [] })).then(ok => { if (ok) setOpen(false); })} />
    </>
  );
  return (
    <Section title={t('creators.title')} icon="sparkles">
      {errorText ? <Caption color={p.danger}>{errorText}</Caption> : null}
      {profile ? (
        <>
          <Body>{t('creators.youAre', { handle: profile.handle })}</Body>
          <Button label={t('creators.viewProfile')} icon="user" variant="secondary" onPress={() => nav.navigate('Creator', { handle: profile.handle })} />
          {bio !== null ? (
            <>
              <Field label={t('creators.bio')} value={bio} onChangeText={setBio} multiline maxLength={300} />
              <Button label={t('common.save')} disabled={busy || !bio.trim()} onPress={() => run(() => social.updateCreator({ bio })).then(ok => { if (ok) setBio(null); })} />
            </>
          ) : <Button label={t('creators.editBio')} icon="edit" variant="secondary" onPress={() => setBio(profile.bio)} />}
          <Button label={t('creators.goPrivate')} variant="plain" onPress={async () => {
            if (await confirm(t('creators.goPrivate'), t('creators.goPrivateConfirm'), t('creators.goPrivate'))) await run(() => social.leaveCreator());
          }} />
        </>
      ) : application?.status === 'pending' ? (
        <Body>{t('creators.pending', { handle: application.handle })}</Body>
      ) : (
        <>
          {application?.status === 'rejected' ? <Body>{t('creators.rejected', { note: application.note ?? '' })}</Body> : null}
          {open ? <Caption>{t('creators.pitch')}</Caption> : null}
          {open ? applyForm : <Button label={t('creators.become')} icon="sparkles" variant="secondary" onPress={() => setOpen(true)} />}
        </>
      )}
    </Section>
  );
}
